// SPDX-License-Identifier: GPL-3.0-or-later
// The dialogs of the game page: find them, open and close them, and keep the keys on the table.
// The rules for which dialog opens when are in dialog-policy.ts.

import { type DialogId, escapeCloses, openSet } from "./dialog-policy.ts";
import { slot } from "./dom.ts";

/** The dialogs of the page, by id. */
export type Dialogs = Readonly<Record<DialogId, HTMLDialogElement>>;

const DIALOG_SLOTS: Readonly<Record<DialogId, string>> = {
  start: "dlg-start",
  pause: "dlg-pause",
  over: "dlg-over",
  howto: "dlg-howto",
  settings: "dlg-settings",
  featured: "dlg-featured",
};

const IDS = Object.keys(DIALOG_SLOTS) as DialogId[];

/** Find every dialog of the page. */
export function findDialogs(doc: Document): Dialogs {
  const entries = IDS.map((id) => [id, slot<HTMLDialogElement>(doc, DIALOG_SLOTS[id])] as const);
  return Object.fromEntries(entries) as Dialogs;
}

/** The hook that runs after openDialog opens a dialog. The page sets it for the alert. */
let afterOpen: (() => void) | null = null;

/**
 * Set the hook that runs after openDialog opens a dialog. Null takes it away. The game page sets
 * it so the context-loss alert stays the top modal.
 */
export function setAfterOpenHook(hook: (() => void) | null): void {
  afterOpen = hook;
}

/** Open a dialog as a modal. An open dialog stays as it is. Then the hook runs, if one is set. */
export function openDialog(d: HTMLDialogElement): void {
  if (d.open) return;
  d.showModal();
  afterOpen?.();
}

/** Close a dialog. A closed dialog stays as it is. */
export function closeDialog(d: HTMLDialogElement): void {
  if (d.open) d.close();
}

/** True while any dialog is open. A game takes no key then. */
export function modalOpen(dialogs: Dialogs): boolean {
  return openSet(dialogs).size > 0;
}

/**
 * Wire the dialogs. The close buttons close their dialog. Escape does not close the start and game
 * over dialogs. After a dialog closes, the keys go back to the table.
 */
export function wireDialogs(doc: Document, dialogs: Dialogs, stage: HTMLElement): void {
  for (const btn of doc.querySelectorAll<HTMLButtonElement>("[data-close]")) {
    btn.addEventListener("click", () => btn.closest("dialog")?.close());
  }
  for (const id of IDS) {
    const d = dialogs[id];
    if (!escapeCloses(id)) d.addEventListener("cancel", (e) => e.preventDefault());
    d.addEventListener("close", () => {
      if (!modalOpen(dialogs)) stage.focus();
    });
  }
}
